import * as React from 'react';

import { Correctness } from '@showbie-socrative/socrative-utils';
import { translate } from '~l10n/client';
import cx from 'classnames';

import {
  IconCheckmark,
  IconStroke,
  IconX,
} from '~shared/components/icons/standard';
import { RadioGroup, RadioItem } from '~shared/components/RadioGroup';

const correctnessStyleMap = {
  [Correctness.Correct]: 'text-white bg-green-500 group-hocus:bg-green-600',
  [Correctness.Partial]: 'text-amber-900 bg-amber-300 group-hocus:bg-amber-400',
  [Correctness.Incorrect]: 'text-white bg-red-600 group-hocus:bg-red-700',
  unchecked: 'text-grey-800 bg-grey-50 group-hocus:bg-grey-100',
  'disabled-checked': 'text-white bg-grey-500',
  'disabled-unchecked': 'text-grey-600 bg-grey-50',
};

interface Props {
  correctness: Correctness;
  checked?: boolean;
  disabled?: boolean;
}

export function ResponseCorrectnessIndicator({
  correctness,
  checked = false,
  disabled,
  ...props
}: Props) {
  const classNames = cx(
    'flex items-center justify-center w-12 h-12 rounded-round',
    !disabled && [
      checked
        ? correctnessStyleMap[correctness]
        : correctnessStyleMap['unchecked'],
    ],
    disabled && [
      checked
        ? correctnessStyleMap['disabled-checked']
        : correctnessStyleMap['disabled-unchecked'],
    ]
  );

  return (
    <div className={classNames} {...props}>
      {correctness === Correctness.Correct ? <IconCheckmark /> : null}
      {correctness === Correctness.Partial ? <IconStroke /> : null}
      {correctness === Correctness.Incorrect ? <IconX /> : null}
    </div>
  );
}

export const StudentScoreCorrectness = ({
  correctness: correctness,
  isControlsDisabled,
  onCorrectnessChange,
}: {
  correctness: Correctness | null;
  isControlsDisabled: boolean;
  onCorrectnessChange: (newCorrectness: Correctness) => void;
}) => (
  <RadioGroup
    id="correctness"
    value={correctness}
    disabled={isControlsDisabled}
    aria-describedby="correctness-label"
    onChange={(e) => onCorrectnessChange(+e.currentTarget.value as Correctness)}
  >
    <RadioItem
      value={Correctness.Correct}
      label={translate('Mark Response Correct')}
    >
      {({ checked, disabled }) => (
        <ResponseCorrectnessIndicator
          correctness={Correctness.Correct}
          checked={checked}
          disabled={disabled}
        />
      )}
    </RadioItem>
    <RadioItem
      value={Correctness.Partial}
      label={translate('Partially Mark Response')}
    >
      {({ checked, disabled }) => (
        <ResponseCorrectnessIndicator
          correctness={Correctness.Partial}
          checked={checked}
          disabled={disabled}
        />
      )}
    </RadioItem>
    <RadioItem
      value={Correctness.Incorrect}
      label={translate('Mark Response Incorrect')}
    >
      {({ checked, disabled }) => (
        <ResponseCorrectnessIndicator
          correctness={Correctness.Incorrect}
          checked={checked}
          disabled={disabled}
        />
      )}
    </RadioItem>
  </RadioGroup>
);
